import { gql, useQuery } from '@apollo/client'
import { GlobalStyles } from '@mui/material'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import Typography from '@mui/material/Typography'
import { CalendarItem, Query } from '@promoboxx/graphql-gateway-types'
import useSimpleFilter from '@promoboxx/use-filter/dist/useSimpleFilter'
import I18n from 'i18n-js'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import {
  Calendar,
  momentLocalizer,
  Event as InitialCalendarEvent,
} from 'react-big-calendar'
import { useSelector } from 'react-redux'
import { makeStyles } from 'tss-react/mui'

import RetailerAccountBrandAutocomplete from '@src/components/shared/Filter/RetailerAccountBrandAutocomplete'
import FilterContentLayoutWrapper from '@src/components/shared/FilterContentLayoutWrapper'
import FilterToolBar from '@src/components/shared/FilterToolbar'
import { SelectOptions } from '@src/components/shared/MultiSelectChip'
import useTracking from '@src/components/shared/Tracking/useTracking'
import urlParamStore from '@src/lib/urlParamStore'
import usePreviouslySelectedBrand from '@src/lib/usePreviouslySelectedBrand'
import { currentRetailerAccountSelector } from '@src/stores/app/selectors'

// import MobileFilterContainer from '../shared/Filter/MobileFilterContainer'

import SearchableFilter from '../shared/Filter/SearchableFilter'

import CalendarEvent from './CalendarEvent'
import DateHeader from './DateHeader'
import ShowMoreButton from './ShowMoreButton'
import ToolBar from './ToolBar'

const localizer = momentLocalizer(moment)

const useStyles = makeStyles()((theme) => ({
  loadingCalender: {
    opacity: 0.5,
  },
  textField: {
    minWidth: '220px',
  },
  chipsWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
  },
}))

interface CalendarProps {
  height?: string
}

const filters: SelectOptions[] = [
  {
    value: 'scheduled_share',
    label: I18n.t('calendar.scheduledShare'),
    customColor: '#ffc208',
  },
  {
    value: 'executed_share',
    label: I18n.t('calendar.executedShare'),
    customColor: '#9b26af',
    customTextColor: 'white',
  },
  {
    value: 'automated',
    label: I18n.t('calendar.communicationAutomation'),
    customColor: '#3f51b5',
    customTextColor: 'white',
  },
]

const CalendarWrapper: React.FC<React.PropsWithChildren<CalendarProps>> = ({
  height = '80vh',
}) => {
  const { classes, cx } = useStyles()
  const [activeEvents, setActiveEvents] = useState<ActiveEvents | null>(null)
  const currentRetailerAccount = useSelector(currentRetailerAccountSelector)

  const { previouslySelectedBrands, setPreviouslySelectedBrand } =
    usePreviouslySelectedBrand()

  const filterApi = useSimpleFilter('schedule', {
    defaultFilterInfo: {
      filter: {
        start: moment(new Date()).startOf('month').toDate().toJSON(),
        end: moment(new Date()).endOf('month').toDate().toJSON(),
        brands: previouslySelectedBrands.map((x) => x.id),
        types: [] as string[],
        targetDate: undefined as string | undefined,
      },
    },
    debounceDuration: 200,
    store: urlParamStore,
  })

  useEffect(() => {
    setPreviouslySelectedBrand(filterApi.filterInfo.filter.brands)
  }, [filterApi.filterInfo.filter.brands, setPreviouslySelectedBrand])

  const retailerIdsFromFilter = useMemo(() => {
    const retailerIds: number[] = []
    const brandIdsAsStrings = filterApi.filterInfo.filter.brands.map(Number)

    for (const retailer of currentRetailerAccount.retailers || []) {
      if (brandIdsAsStrings.includes(Number(retailer.brand?.id))) {
        retailerIds.push(Number(retailer.id))
      }
    }

    return retailerIds
  }, [currentRetailerAccount, filterApi.filterInfo.filter.brands])

  const { track } = useTracking()

  const { data, loading: gqlLoading } = useQuery<Query>(getCalendarItems, {
    variables: {
      resourceId: currentRetailerAccount.id,
      resourceType: 'retailer_account',
      start: filterApi.debouncedFilterInfo.filter.start,
      end: filterApi.debouncedFilterInfo.filter.end,
      retailerIds: retailerIdsFromFilter,
    },
    fetchPolicy: 'network-only',
  })

  const calendarItems = data?.getCalendarItems ?? []
  const events = normalizeEvents(calendarItems).filter((evt) => {
    const eventState = evt.automated ? ['automated', evt.type] : [evt.type]
    if (
      filterApi.debouncedFilterInfo.filter.types.length === 1 &&
      filterApi.debouncedFilterInfo.filter.types[0] === 'all'
    ) {
      return true
    } else if (filterApi.debouncedFilterInfo.filter.types.length) {
      return eventState.some((evt: string) =>
        filterApi.debouncedFilterInfo.filter.types.includes(evt),
      )
    }
    return true
  })

  const loading = filterApi.isLoading || gqlLoading
  const CalendarWrapper = FilterContentLayoutWrapper

  return (
    <CalendarWrapper
      title={I18n.t('nav.schedule')}
      slot={{
        filter: (
          <FilterToolBar
            filterItems={[
              <RetailerAccountBrandAutocomplete
                showAll
                value={filterApi.filterInfo.filter.brands}
                onChange={(value) => {
                  filterApi.updateFilter(
                    { brands: value.map((x) => String(x.id)) },
                    true,
                  )
                }}
                slotProps={{
                  textField: {
                    label: I18n.t('filters.selectBrands'),
                    sx: {
                      backgroundColor: (theme) =>
                        theme.palette.background.paper,
                    },
                  },
                }}
              />,
              <SearchableFilter
                value={filterApi.filterInfo.filter.types}
                options={filters}
                label={I18n.t('calendar.filters')}
                multiple
                showAll
                onChange={(value) => {
                  if (value) {
                    filterApi.updateFilter({ types: Array.from(value) }, true)
                  }
                }}
              />,
            ]}
            onClear={() =>
              filterApi.updateFilter({
                brands: [],
                types: [],
              })
            }
          />
        ),
      }}
    >
      <div style={{ position: 'relative' }}>
        <GlobalStyles
          styles={(theme) => ({
            '.rbc-header': {
              borderBottom: 'none',
            },
            '.rbc-off-range-customColor': {
              backgroundColor: '#efefef',
            },
            '.rbc-day-slot .rbc-background-event, .rbc-event': {
              backgroundColor: 'none !important',
            },
            '.rbc-show-more': {
              textDecoration: 'none',
            },
            '.rbc-month-view': {
              border: '0',

              // HACK Get the calendar to look like it's on a `<Paper />` component.
              backgroundColor: theme.palette.background.paper,
              borderRadius: theme.shape.borderRadius,
              boxShadow: theme.shadows[1],
            },
          })}
        />

        {loading && (
          <Box
            position="absolute"
            justifyContent="center"
            alignItems="center"
            top={'50%'}
            left={'50%'}
          >
            <CircularProgress />
          </Box>
        )}

        <Box margin={4}>
          <Calendar
            onSelectEvent={(event) => {
              track({
                event: 'Click',
                data: {
                  id: event.item.id,
                  channel: event.channel,
                  start: event.start,
                  end: event.end,
                  type: event.type,
                },
              })
            }}
            onNavigate={(date) => {
              track({ event: 'View', data: { date } })
            }}
            localizer={localizer}
            events={events}
            doShowMoreDrillDown={false}
            defaultDate={
              filterApi.debouncedFilterInfo.filter.targetDate
                ? new Date(filterApi.debouncedFilterInfo.filter.targetDate)
                : undefined
            }
            onRangeChange={(range) => {
              const normalized = normalizeCalendarRange(range)

              // We want to save the range of where the user was looking, however,
              // RBC gives a range but accepts a single date. So, we find the
              // midpoint of the range, cross our fingers, and hope that works
              // well enough.
              let targetDate: string | undefined = undefined
              if (normalized.start && normalized.end) {
                const duration =
                  normalized.end.valueOf() - normalized.start.valueOf()
                const midPoint = duration / 2
                targetDate = new Date(
                  normalized.start.valueOf() + midPoint,
                ).toJSON()
              }

              filterApi.updateFilter({
                start: normalized.start?.toJSON(),
                end: normalized.end?.toJSON(),
                targetDate,
              })
            }}
            /**
             * this removes the default styles of each event block
             */
            style={{ height }}
            eventPropGetter={() => ({
              style: { background: 'none', padding: 0 },
            })}
            onShowMore={(events, date) => {
              setActiveEvents({ events, date })
              track({
                event: 'Click',
                data: {
                  events,
                  date,
                },
              })
            }}
            formats={{
              monthHeaderFormat: (date, culture, localizer) => {
                return date.toLocaleDateString(I18n.locale, { month: 'long' })
              },
              weekdayFormat: (date, culture, localizer) => {
                return date.toLocaleDateString(I18n.locale, {
                  weekday: 'short',
                })
              },
            }}
            messages={{
              week: I18n.t('calendar.week'),
              work_week: I18n.t('calendar.work_week'),
              day: I18n.t('calendar.day'),
              month: I18n.t('calendar.month'),
              previous: I18n.t('calendar.previous'),
              next: I18n.t('calendar.next'),
              today: I18n.t('calendar.today'),
              agenda: I18n.t('calendar.agenda'),
              //Declaration file doesn't include JSX the ReturnValue
              //of showMore but it works
              //@ts-ignore
              showMore: (count: number) => (
                <ShowMoreButton
                  count={count}
                  events={activeEvents?.events}
                  date={activeEvents?.date}
                />
              ),
            }}
            className={cx(loading && classes.loadingCalender)}
            components={{
              month: {
                header: ColumnHeader,
                dateHeader: DateHeader,
                event: CalendarEvent,
              },
              toolbar: ToolBar,
            }}
            views={{
              month: true,
              week: true,
            }}
          />
        </Box>
      </div>
    </CalendarWrapper>
  )
}

export default CalendarWrapper

const ColumnHeader = ({ label }: { label: string }) => {
  return (
    <Typography variant="button" color="textSecondary">
      {label}
    </Typography>
  )
}

const normalizeCalendarRange = (range: RbcRange) => {
  if (Array.isArray(range)) {
    return {
      start: range[0],
      end: range[range.length - 1],
    }
  }

  return {
    start: new Date(range.start),
    end: new Date(range.end),
  }
}
const getChannelFromTitle = (title: string) => {
  if (title.toLowerCase().includes('facebook')) {
    return 'facebook'
  } else if (title.toLowerCase().includes('twitter')) {
    return 'twitter'
  } else if (title.toLowerCase().includes('instagram')) {
    return 'instagram'
  } else if (title.toLowerCase().includes('email')) {
    return 'email'
  }
  return null
}

export const normalizeEvents = (
  calendarItems: CalendarItem[],
): CalendarEventType[] => {
  const events: CalendarEventType[] = []
  calendarItems.forEach((calendarItem) => {
    const channel = getChannelFromTitle(calendarItem.title)
    const associable = calendarItem.associable
    if (associable?.__typename !== 'Share') {
      return
    }
    switch (calendarItem.category) {
      case 'executed_share':
        events.push({
          type: calendarItem.category,
          isStart: true,
          start: new Date(calendarItem.start_date_at),
          end: new Date(calendarItem.start_date_at),
          allDay: false,
          title: I18n.t('calendar.executedShareEvent', {
            channel,
          }),
          channel: channel && `${channel}_shares`,
          item: calendarItem,
          automated: !!associable?.communication_automation_id,
        })
        break
      case 'scheduled_share':
        events.push({
          type: calendarItem.category,
          isStart: true,
          start: new Date(calendarItem.start_date_at),
          end: new Date(calendarItem.start_date_at),
          allDay: false,
          title: I18n.t('calendar.scheduledShareEvent', {
            channel,
          }),
          channel: channel && `${channel}_shares`,
          item: calendarItem,
          automated: !!associable?.communication_automation_id,
        })
        break
      default:
        break
    }
  })
  return events
}

export const getCalendarItems = gql`
  query getDashboardCalendarItems(
    $resourceId: ID!
    $resourceType: String!
    $start: String!
    $end: String!
    $retailerIds: [ID!]
  ) {
    getCalendarItems(
      resourceId: $resourceId
      resourceType: $resourceType
      params: {
        filters: { min_date: $start, max_date: $end }
        retailer_ids: $retailerIds
        fields: ["associable"]
      }
    ) {
      id
      start_date_at
      end_date_at
      title
      category
      owner_id
      owner_type
      targetable_id
      targetable_type
      associable_id
      associable_type
      associable {
        ... on Share {
          id
          communication_automation_id
        }
      }
    }
  }
`
interface ActiveEvents {
  events: CalendarEventType[]
  date: Date
}

type RbcRange =
  | {
      start: Date | string
      end: Date | string
    }
  | Date[]

export type CalendarEventType = InitialCalendarEvent & {
  type: 'campaign' | 'scheduled_share' | 'executed_share'
  automated: boolean
  isStart: boolean
  channel?:
    | 'facebook_shares'
    | 'twitter_shares'
    | 'instagram_shares'
    | 'email_shares'
    | null
  item: CalendarItem
}
